import React, { MutableRefObject, useEffect, useRef, useState } from "react"

import {
  AlertMessage,
  AssetMap,
  Hole,
  HoleInOneConfirmation,
  HoleInOneResume,
  HoleSponsor,
  PlayerHoleScore,
  Round,
  TeamRound,
  TeamRoundPlayer,
  TeamRoundPlayerHoleInOneFeedPostIdMap,
  TeamRoundPlayerScoreMap
} from "../../types"

import HoleHeader from "./HoleHeader"
import Player from "./Player"
import Keyboard from "./Keyboard"
import TeeHeader from "./TeeHeader"
import SaveScores from "./SaveScores"
import { getPlayerHoleStrokes, holeMaxStrokes, strokesForHole, teamRoundHoles } from "../../util"

export type FocusScoreEntry = (holeIndex: number, player: TeamRoundPlayer) => void

interface HoleProps {
  holeIndex: number
  setHoleIndex: (holeIndex: number) => void
  player: TeamRoundPlayer
  round: Round
  teamRound: TeamRound
  assets: AssetMap
  scores: TeamRoundPlayerScoreMap
  holeInOneFeedPostIds?: TeamRoundPlayerHoleInOneFeedPostIdMap
  postScores: (scores: PlayerHoleScore[]) => Promise<any>
  reviewScores()
  showHoleInOneConfirmation: (confirmation: HoleInOneConfirmation) => void
  showAlert: (message: AlertMessage) => void
  showSponsor: (sponsor: HoleSponsor) => void
  focusScoreEntryRef?: MutableRefObject<FocusScoreEntry | null>
}

export default function ScoreEntry({
  holeIndex,
  setHoleIndex,
  player,
  round,
  teamRound,
  assets,
  scores,
  holeInOneFeedPostIds,
  postScores,
  reviewScores,
  showHoleInOneConfirmation,
  showAlert,
  showSponsor,
  focusScoreEntryRef
}: HoleProps) {
  const holes = teamRoundHoles(round.holes, round.numberOfHoles, teamRound.startHoleNumber)
  const hole: Hole | undefined = holes[holeIndex]
  const sponsor = hole?.sponsor

  const savedPlayerScore = getPlayerHoleStrokes(scores, teamRound.players[0].id, holeIndex)
  const savedPartnerScore =
    teamRound.players[1] && !teamRound.players[1].canceled
      ? getPlayerHoleStrokes(scores, teamRound.players[1].id, holeIndex)
      : null

  // undefined = not edited on this hole yet; null = cleared; number = entered score
  const [playerScore, setPlayerScore] = useState<number | null | undefined>(undefined)
  const [partnerScore, setPartnerScore] = useState<number | null | undefined>(undefined)

  const [selectedPlayer, setSelectedPlayer] = useState<TeamRoundPlayer>(teamRound.players[0])
  const [keyboardVisible, setKeyboardVisible] = useState(false)
  const [saving, setSaving] = useState(false)
  const openKeyboardAfterHoleChange = useRef(false)

  // Parent can change holeIndex (e.g. after hole-in-one skip) without going through setHole.
  useEffect(() => {
    setPlayerScore(undefined)
    setPartnerScore(undefined)
    if (openKeyboardAfterHoleChange.current) {
      setKeyboardVisible(true)
      openKeyboardAfterHoleChange.current = false
    } else {
      setKeyboardVisible(false)
    }
  }, [holeIndex])

  const strokes = hole ? strokesForHole(selectedPlayer.handicapStrokes, hole.handicap) : 0
  const max = hole ? holeMaxStrokes(hole.par, strokes) : 0
  const score = selectedPlayer.id === teamRound.players[0].id ? playerScore ?? null : partnerScore ?? null
  const savedScore = selectedPlayer.id === teamRound.players[0].id ? savedPlayerScore : savedPartnerScore

  let holesCompleted = 0
  if (scores && scores[teamRound.players[0].id]) {
    for (const s of scores[teamRound.players[0].id]) {
      if (s || s === 0) {
        holesCompleted++
      }
    }
  }
  const finished = holesCompleted >= round.numberOfHoles
  const unsavedScores =
    (playerScore !== undefined && playerScore !== savedPlayerScore) ||
    (partnerScore !== undefined && partnerScore !== savedPartnerScore)

  const prevHole = function () {
    if (holeIndex <= 0) {
      if (finished) {
        reviewScores()
      } else {
        setHole(round.numberOfHoles === 18 ? 17 : 8)
      }
    } else if (round.numberOfHoles === 9 && holeIndex === 9) {
      if (finished) {
        reviewScores()
      } else {
        setHole(17)
      }
    } else {
      setHole(holeIndex - 1)
    }
  }

  const nextHole = function () {
    if (holeIndex === (round.numberOfHoles === 18 ? 17 : 8)) {
      if (finished) {
        reviewScores()
      } else {
        setHole(0)
      }
    } else if (holeIndex >= 17) {
      if (finished) {
        reviewScores()
      } else {
        setHole(round.numberOfHoles === 18 ? 0 : 9)
      }
    } else {
      setHole(holeIndex + 1)
    }
  }

  const setHole = function (index) {
    setPlayerScore(undefined)
    setPartnerScore(undefined)
    setHoleIndex(index)
  }

  const toggleKeyboard = (newSelectedPlayer: TeamRoundPlayer) => {
    const showKeyboard = newSelectedPlayer.id !== selectedPlayer.id || !keyboardVisible
    setSelectedPlayer(newSelectedPlayer)
    setKeyboardVisible(showKeyboard)
  }

  const updateScore = (player: TeamRoundPlayer, score: number | null) => {
    if (player) {
      if (player.id === teamRound.players[0].id) {
        setPlayerScore(score)
        if (teamRound.players[1]) {
          setSelectedPlayer(teamRound.players[1])
        } else {
          setKeyboardVisible(false)
        }
      } else if (teamRound.players[1] && player.id === teamRound.players[1].id) {
        setPartnerScore(score)
        setKeyboardVisible(false)
      }
    }
  }

  const prevHoleResume = (finishedAfterSave: boolean): HoleInOneResume => {
    if (holeIndex <= 0) {
      return finishedAfterSave ? { kind: "review" } : { kind: "hole", index: round.numberOfHoles === 18 ? 17 : 8 }
    } else if (round.numberOfHoles === 9 && holeIndex === 9) {
      return finishedAfterSave ? { kind: "review" } : { kind: "hole", index: 17 }
    } else {
      return { kind: "hole", index: holeIndex - 1 }
    }
  }

  const nextHoleResume = (finishedAfterSave: boolean): HoleInOneResume => {
    if (holeIndex === (round.numberOfHoles === 18 ? 17 : 8)) {
      return finishedAfterSave ? { kind: "review" } : { kind: "hole", index: 0 }
    } else if (holeIndex >= 17) {
      return finishedAfterSave ? { kind: "review" } : { kind: "hole", index: round.numberOfHoles === 18 ? 0 : 9 }
    } else {
      return { kind: "hole", index: holeIndex + 1 }
    }
  }

  const continueAfterSuccessfulSave = (
    scoresAfterSave: TeamRoundPlayerScoreMap,
    holeInOnes: { teamRoundPlayerId: number; userId: number; name: string; strokes: number }[],
    afterSave?: () => void
  ) => {
    let userHolesCompleted = 0
    if (player && scoresAfterSave && scoresAfterSave[player.id]) {
      for (const s of scoresAfterSave[player.id]) {
        if (s || s === 0) {
          userHolesCompleted++
        }
      }
    }

    const finishedAfterSave = userHolesCompleted >= round.numberOfHoles

    if (holeInOnes.length > 0) {
      let resume: HoleInOneResume
      if (afterSave === prevHole) {
        resume = prevHoleResume(finishedAfterSave)
      } else if (afterSave === nextHole) {
        resume = nextHoleResume(finishedAfterSave)
      } else if (afterSave) {
        resume = { kind: "hole", index: holeIndex }
      } else if (finishedAfterSave) {
        resume = { kind: "review" }
      } else {
        resume = nextHoleResume(finishedAfterSave)
      }

      return showHoleInOneConfirmation({
        holeIndex,
        holeId: hole.id,
        playerScores: holeInOnes,
        resume
      })
    }

    if (afterSave) {
      afterSave()
    } else if (finishedAfterSave) {
      reviewScores()
      window.scrollTo(0, 0)
    } else {
      nextHole()
      window.scrollTo(0, 0)
    }
  }

  const saveScores = async (afterSave?: () => void) => {
    if (!hole) {
      return
    }

    setKeyboardVisible(false)

    const playerScores = []
    const holeInOnes: { teamRoundPlayerId: number; userId: number; name: string; strokes: number }[] = []

    // Prefer freshly entered scores; fall back to already-saved values when the
    // field was never edited (e.g. Edit Scores after hole-in-one).
    const effectivePlayerScore = playerScore !== undefined ? playerScore : savedPlayerScore
    const effectivePartnerScore = partnerScore !== undefined ? partnerScore : savedPartnerScore

    if (teamRound.players[0] && effectivePlayerScore !== null && effectivePlayerScore !== undefined) {
      playerScores.push({
        teamRoundPlayerId: teamRound.players[0].id,
        holeId: hole.id,
        strokes: effectivePlayerScore
      })

      if (effectivePlayerScore === 1 && !holeInOneFeedPostIds?.[teamRound.players[0].id]?.[holeIndex]) {
        holeInOnes.push({
          teamRoundPlayerId: teamRound.players[0].id,
          userId: teamRound.players[0].user.id,
          name: teamRound.players[0].user.firstName,
          strokes: effectivePlayerScore
        })
      }
    }

    if (
      teamRound.players[1] &&
      !teamRound.players[1].canceled &&
      effectivePartnerScore !== null &&
      effectivePartnerScore !== undefined
    ) {
      playerScores.push({
        teamRoundPlayerId: teamRound.players[1].id,
        holeId: hole.id,
        strokes: effectivePartnerScore
      })

      if (effectivePartnerScore === 1 && !holeInOneFeedPostIds?.[teamRound.players[1].id]?.[holeIndex]) {
        holeInOnes.push({
          teamRoundPlayerId: teamRound.players[1].id,
          userId: teamRound.players[1].user.id,
          name: teamRound.players[1].user.firstName,
          strokes: effectivePartnerScore
        })
      }
    }

    if (playerScores.length === 0) {
      showAlert({
        title: "No Score Entered",
        message: "Please enter a score before saving.",
        buttons: [{ text: "Ok", style: "primary" }]
      })
      return
    }

    // Scores already match what's saved — just continue navigation.
    if (!unsavedScores) {
      continueAfterSuccessfulSave(scores, holeInOnes, afterSave)
      return
    }

    setSaving(true)
    const newScores = await postScores(playerScores)
    setSaving(false)
    if (newScores) {
      continueAfterSuccessfulSave(newScores.scores || newScores, holeInOnes, afterSave)
    }
  }

  const applyScoreFocus = (index: number, focusPlayer: TeamRoundPlayer) => {
    const playerFromTeam = teamRound.players.find((p) => p.id === focusPlayer.id) || focusPlayer
    setSelectedPlayer(playerFromTeam)
    if (index === holeIndex) {
      setKeyboardVisible(true)
      return
    }

    openKeyboardAfterHoleChange.current = true
    setPlayerScore(undefined)
    setPartnerScore(undefined)
    setHoleIndex(index)
  }

  const focusScoreEntry: FocusScoreEntry = (index, focusPlayer) => {
    const doFocus = () => applyScoreFocus(index, focusPlayer)

    if (index !== holeIndex && unsavedScores) {
      showAlert({
        title: "Scores Not Saved",
        message: "You have entered scores on this hole that you haven't saved. Do you want to save these scores?",
        buttons: [
          { text: "Yes", style: "primary", onPress: () => saveScores(doFocus) },
          { text: "No", onPress: doFocus }
        ]
      })
      return
    }

    doFocus()
  }

  // No dependency array: focusScoreEntry closes over the current hole/score
  // state, so re-assign on every render to keep the parent's handle fresh.
  useEffect(() => {
    if (!focusScoreEntryRef) {
      return
    }

    focusScoreEntryRef.current = focusScoreEntry
    return () => {
      focusScoreEntryRef.current = null
    }
  })

  if (!hole) {
    return null
  }

  return (
    <>
      <div className="card" style={{ marginBottom: "1rem" }}>
        <div className="container">
          <TeeHeader round={round} />
          <HoleHeader
            hole={hole}
            assets={assets}
            prevHole={prevHole}
            nextHole={nextHole}
            unsavedScores={unsavedScores}
            showAlert={showAlert}
            saveScores={saveScores}
          />
          {teamRound.players[0] && !teamRound.players[0].canceled ? (
            <Player
              player={teamRound.players[0]}
              selected={keyboardVisible && selectedPlayer && teamRound.players[0].id === selectedPlayer.id}
              hole={hole}
              score={playerScore ?? null}
              savedScore={savedPlayerScore}
              toggleKeyboard={toggleKeyboard}
            />
          ) : (
            <></>
          )}
          {teamRound.players[1] && !teamRound.players[1].canceled ? (
            <Player
              player={teamRound.players[1]}
              selected={keyboardVisible && selectedPlayer && teamRound.players[1].id === selectedPlayer.id}
              hole={hole}
              score={partnerScore ?? null}
              savedScore={savedPartnerScore}
              toggleKeyboard={toggleKeyboard}
            />
          ) : (
            <></>
          )}
          <SaveScores saving={saving} assets={assets} onClick={() => saveScores()} />
        </div>
      </div>
      {sponsor ? (
        <>
          <div style={{ textAlign: "center", fontSize: "0.875rem", margin: "1rem 0 0.5rem" }}>
            This hole sponsored by <strong>{sponsor.name}</strong>
          </div>
          <div className="card" style={{ paddingTop: 0, marginBottom: "1rem" }}>
            {sponsor.photoUrl ? (
              <img src={sponsor.photoUrl} alt={sponsor.name} style={{ borderRadius: "0.5rem 0.5rem 0 0" }} />
            ) : (
              <div>&nbsp;</div>
            )}
            <div className="container">
              <div style={{ display: "flex", justifyContent: "space-between", padding: "0.5rem" }}>
                <div>
                  <div style={{ fontWeight: "bold", fontSize: "0.875rem" }}>{sponsor.name}</div>
                  <div style={{ fontSize: "0.75rem" }}>{sponsor.subtitle}</div>
                </div>
                <div>
                  <a
                    onClick={() => showSponsor(sponsor)}
                    style={{
                      border: "solid 2px #141A1A",
                      borderRadius: "0.5rem",
                      textDecoration: "none",
                      padding: "0.5rem",
                      fontSize: "0.875rem",
                      fontWeight: "bold",
                      cursor: "pointer",
                      whiteSpace: "nowrap"
                    }}
                  >
                    Learn More
                  </a>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <></>
      )}
      {keyboardVisible ? (
        <Keyboard
          player={selectedPlayer}
          max={max}
          score={score}
          savedScore={savedScore}
          updateScore={updateScore}
          dismiss={() => setKeyboardVisible(false)}
          showAlert={showAlert}
        />
      ) : null}
    </>
  )
}
